import { useState } from 'react'
import { Check, ChevronDown, ChevronUp, Clock, Info, Lightbulb, TriangleAlert, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import Spinner from '@/components/ui/icons/loading-spinner'
import { getOrderStatusBadgeClasses } from '@/lib/utils'
import type {
  DAOutcome,
  DAProductExclusion,
  DAReason,
  DASeverityTag,
  DAVariationExclusionGroup
} from '@/lib/api/med-requests/decision-assistant'

function CountChip({ count }: { count: number }) {
  return (
    <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-custom-neutral-700 text-[13px] font-medium leading-none text-black'>
      {count}
    </span>
  )
}

function SeverityBadge({ severity }: { severity: DASeverityTag }) {
  // Colours follow the app's status palette: On Hold / warning -> amber, reject -> red.
  const status = severity === 'reject' ? 'Rejected' : severity === 'fan' ? 'On Hold' : 'Clinical Query'
  const label = severity === 'reject' ? 'Reject' : severity === 'fan' ? 'On Hold' : 'Warning'
  return (
    <span
      className={`inline-flex h-6 shrink-0 items-center justify-center rounded border px-1 text-[10px] leading-5 ${getOrderStatusBadgeClasses(status)}`}
    >
      {label}
    </span>
  )
}

function ReasonItem({ reason }: { reason: DAReason }) {
  return (
    <li className='flex items-start gap-2 rounded-2xl border border-custom-neutral-700 p-4'>
      {reason.severity && <SeverityBadge severity={reason.severity} />}
      <p className='text-[14px] font-medium leading-5 tracking-[-0.15px] text-black'>{reason.text}</p>
    </li>
  )
}

function ExclusionItem({ exclusion }: { exclusion: DAProductExclusion }) {
  return (
    <li className='flex items-start gap-2 rounded-2xl border border-custom-neutral-700 px-4 py-2'>
      <X className='mt-0.5 size-5 shrink-0 text-[#ff4d1a]' strokeWidth={2.5} aria-hidden />
      <div className='text-[14px] font-medium leading-5 tracking-[-0.15px] text-black'>
        <p>{exclusion.name}</p>
        {exclusion.subLabel && <p>{exclusion.subLabel}</p>}
      </div>
    </li>
  )
}

function VariationExclusionItem({ group }: { group: DAVariationExclusionGroup }) {
  return (
    <li className='flex flex-col gap-2 rounded-2xl border border-custom-neutral-700 px-4 py-2'>
      <div className='flex items-start gap-2'>
        <X className='mt-0.5 size-5 shrink-0 text-[#ff4d1a]' strokeWidth={2.5} aria-hidden />
        <p className='text-[14px] font-medium leading-5 tracking-[-0.15px] text-black'>{group.productTitle}</p>
      </div>
      <div className='flex flex-wrap gap-2 pl-7'>
        {group.variations.map((label, index) => (
          <span
            key={index}
            className={`inline-flex h-6 items-center justify-center rounded border px-2 text-[12px] font-medium leading-5 ${getOrderStatusBadgeClasses('Rejected')}`}
          >
            {label}
          </span>
        ))}
      </div>
    </li>
  )
}

type BannerConfig = {
  /** App status -> palette via getOrderStatusBadgeClasses (Approved -> green,
   *  On Hold -> amber, reject -> red), OR explicit `classes` for Review, which
   *  is informational blue — blue is not part of the app's status palette. */
  status?: string
  classes?: string
  label: string
  Icon: LucideIcon
  /** When set, the icon is shown white inside a filled circle of this colour. */
  iconCircle?: string
  description?: string
}

const BANNER_CONFIG: Record<DAOutcome['type'], BannerConfig> = {
  approve: {
    status: 'Approved',
    label: 'Approve consultation',
    Icon: Check,
    iconCircle: 'bg-[#388E10]',
    description: 'All standard products and dosages are suitable for this patient.'
  },
  review: {
    classes: 'border-[#153cb7] bg-[#e8edfb] text-[#153cb7]',
    label: 'Consultation needs to be reviewed',
    Icon: TriangleAlert
  },
  furtherAnswers: {
    status: 'On Hold',
    label: 'Further answers needed',
    Icon: Clock
  },
  reject: {
    status: 'Rejected',
    label: 'Reject consultation',
    Icon: X,
    iconCircle: 'bg-[#FF4D1A]'
  }
}

function Banner({ type }: { type: DAOutcome['type'] }) {
  const { status, classes, label, Icon, iconCircle, description } = BANNER_CONFIG[type]
  // Review uses explicit info-blue classes; the rest inherit the app status
  // palette. The plain icon and label pick up the text colour via inheritance.
  const palette = classes ?? getOrderStatusBadgeClasses(status ?? '')
  return (
    <div className={`flex flex-col gap-2 rounded-2xl border px-4 py-2 ${palette}`}>
      <div className='flex items-center gap-2'>
        {iconCircle ? (
          <div className={`flex size-6 items-center justify-center rounded-full ${iconCircle}`}>
            <Icon className='size-4 text-white' strokeWidth={3} aria-hidden />
          </div>
        ) : (
          <Icon className='size-6 shrink-0' aria-hidden />
        )}
        <p className='text-[16px] font-bold leading-5'>{label}</p>
      </div>
      {description && <p className='text-[13px] leading-5 text-black'>{description}</p>}
    </div>
  )
}

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className='flex w-full items-center justify-between'>
      <p className='text-[15px] font-medium leading-5 tracking-[-0.15px] text-black'>{label}</p>
      <CountChip count={count} />
    </div>
  )
}

function ReasonsList({ reasons, collapsible }: { reasons: DAReason[]; collapsible: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const shouldCollapse = collapsible && reasons.length > 3
  const visible = shouldCollapse && !expanded ? reasons.slice(0, 3) : reasons
  const Chevron = expanded ? ChevronUp : ChevronDown

  return (
    <div className='flex w-full flex-col items-center gap-4'>
      <ul className='flex w-full flex-col gap-4'>
        {visible.map(r => (
          <ReasonItem key={r.id} reason={r} />
        ))}
      </ul>
      {shouldCollapse && (
        <button
          type='button'
          onClick={() => setExpanded(prev => !prev)}
          className='inline-flex items-center gap-2 rounded-full border border-[#153cb7] px-3 py-1.5 text-[14px] font-medium text-[#153cb7]'
        >
          <TriangleAlert className='size-4 text-[#c80]' aria-hidden />
          {expanded ? 'See less' : 'See more'}
          <Chevron className='size-4' aria-hidden />
        </button>
      )}
    </div>
  )
}

/** Fallback shown when the validation result is missing or not in the expected
 * shape (404, upstream/network error, malformed payload). */
function UnavailableState() {
  return (
    <div className='flex items-center gap-2 rounded-2xl border border-custom-neutral-700 bg-[#f7f8fa] px-4 py-2'>
      <Info className='size-5 shrink-0 text-custom-neutral-600' aria-hidden />
      <p className='text-[13px] leading-5 text-custom-neutral-600'>
        Decision Assistant is not available for this consultation.
      </p>
    </div>
  )
}

function LoadingState() {
  return (
    <div className='flex items-center gap-2 rounded-2xl border border-custom-neutral-700 px-4 py-2'>
      <Spinner iconClassName='size-4' />
      <p className='text-[13px] leading-5 text-custom-neutral-600'>Checking Decision Assistant…</p>
    </div>
  )
}

function OutcomeBody({ outcome }: { outcome: DAOutcome }) {
  const hasExclusions = outcome.type !== 'approve' && !!outcome.productExclusions?.length
  const hasVariationExclusions = outcome.type !== 'approve' && !!outcome.variationExclusions?.length
  const collapsible = outcome.type === 'review' || outcome.type === 'furtherAnswers'

  return (
    <>
      <Banner type={outcome.type} />

      {outcome.type !== 'approve' && (
        <>
          {outcome.reasons.length > 0 && (
            <>
              <SectionHeader
                label={outcome.reasons.length === 1 ? 'Reason' : 'Reasons'}
                count={outcome.reasons.length}
              />
              <ReasonsList reasons={outcome.reasons} collapsible={collapsible} />
            </>
          )}

          {hasExclusions && (
            <>
              <hr className='border-t border-custom-neutral-700' />
              <div className='flex flex-col gap-2'>
                <SectionHeader label='Suggested product exclusion' count={outcome.productExclusions!.length} />
                <ul className='flex flex-col gap-2'>
                  {outcome.productExclusions!.map(e => (
                    <ExclusionItem key={e.id} exclusion={e} />
                  ))}
                </ul>
              </div>
            </>
          )}

          {hasVariationExclusions && (
            <>
              <hr className='border-t border-custom-neutral-700' />
              <div className='flex flex-col gap-2'>
                <SectionHeader
                  label='Excluded product variations'
                  count={outcome.variationExclusions!.reduce((total, g) => total + g.variations.length, 0)}
                />
                <ul className='flex flex-col gap-2'>
                  {outcome.variationExclusions!.map(group => (
                    <VariationExclusionItem key={group.productHash} group={group} />
                  ))}
                </ul>
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}

type Props = {
  outcome?: DAOutcome
  unavailable?: boolean
  isLoading?: boolean
}

export default function DecisionAssistantPanel({ outcome, unavailable, isLoading }: Props = {}) {
  // Nothing to show yet (e.g. not enabled / no request) — render nothing.
  if (!isLoading && !unavailable && !outcome) return null

  return (
    <div className='flex flex-col gap-4 rounded-2xl border border-[#e5e7eb] bg-white px-[18px] py-4 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'>
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-2'>
          <div className='flex size-8 items-center justify-center rounded-full bg-[#153cb7]'>
            <Lightbulb className='size-4 text-white' aria-hidden />
          </div>
          <p className='text-[20px] font-semibold leading-[27px] tracking-[-0.44px] text-[#101828]'>
            Decision Assistant
          </p>
        </div>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : unavailable || !outcome ? (
        <UnavailableState />
      ) : (
        <OutcomeBody outcome={outcome} />
      )}
    </div>
  )
}
